import { useAppSelector } from "../../hooks/use-app-selector.ts"

export const COMMUNICATION_TEMPLATES_PERMISSION =
  "communication.messages.communication_templates_manage" as const

export const COMMUNICATION_TEMPLATES_USE_PERMISSION =
  "communication.messages.communication_templates_use" as const

export const COMMUNICATION_SEND_PERMISSION =
  "communication.messages.communication_send" as const

export const COMMUNICATION_BULK_SEND_PERMISSION =
  "communication.messages.communication_bulk_send" as const

export const COMMUNICATION_VIEW_ALL_PERMISSION =
  "communication.messages.communication_view_all" as const

export const COMMUNICATION_SETTINGS_PERMISSION =
  "communication.messages.communication_settings_manage" as const

export interface CommunicationRights {
  send: boolean
  bulkSend: boolean
  /** Pick templates while writing; managing them is `templates`. */
  templatesUse: boolean
  templates: boolean
  settings: boolean
  viewAll: boolean
}

// Only an explicit `false` turns the module off: a backend without the flag has no switch to read.
export const communicationEnabled = (featureCommunication?: boolean) =>
  featureCommunication !== false

export const useCommunicationEnabled = () =>
  communicationEnabled(
    useAppSelector((state) => state.auth?.userInfo?.feature_communication)
  )

interface CommunicationCodes {
  communication_send?: boolean
  communication_bulk_send?: boolean
  communication_templates_use?: boolean
  communication_templates_manage?: boolean
}

// Managing templates includes using them. A backend that predates the code sends
// no key at all, and there sending still carried templates.
export const mayUseTemplates = (codes: CommunicationCodes | undefined) => {
  if (codes?.communication_templates_manage) return true
  if (codes?.communication_templates_use === undefined)
    return !!(codes?.communication_send || codes?.communication_bulk_send)
  return codes.communication_templates_use
}

export const useCommunicationRights = (): CommunicationRights => {
  const enabled = useCommunicationEnabled()
  const codes = useAppSelector(
    (state) => state.auth?.permissions?.codes?.communication?.messages
  )
  return {
    send: enabled && !!codes?.communication_send,
    bulkSend: enabled && !!codes?.communication_bulk_send,
    templatesUse: enabled && mayUseTemplates(codes),
    templates: enabled && !!codes?.communication_templates_manage,
    settings: enabled && !!codes?.communication_settings_manage,
    viewAll: enabled && !!codes?.communication_view_all
  }
}

// A template picked outside Communication (a lead's email card, the leads bulk
// email) needs the same right; a backend without the code never asked for one there.
export const mayPickLeadTemplates = (codes: CommunicationCodes | undefined) =>
  !!codes?.communication_templates_manage ||
  codes?.communication_templates_use !== false

export const useMayPickLeadTemplates = () =>
  mayPickLeadTemplates(
    useAppSelector(
      (state) => state.auth?.permissions?.codes?.communication?.messages
    )
  )
